import React, { useState, useEffect } from 'react';
import { UserManagementService } from '../../shared/services/UserManagementService';
import { TenantService } from '../services/TenantService';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  Mail,
  Shield, 
  Clock, 
  Building2,
  Eye,
  Edit3,
  Trash2,
  MoreVertical,
  UserCheck,
  UserX,
  Calendar,
  AlertCircle,
  CheckCircle,
  XCircle,
  Send,
  Copy,
  ExternalLink
} from 'lucide-react';

/**
 * UserManagement Component - Complete user management dashboard for platform owners
 * Manages users, invitations, roles, and permissions across all tenants
 */
export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [userStats, setUserStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI State
  const [activeTab, setActiveTab] = useState('users'); // 'users', 'invitations'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedTenant, setSelectedTenant] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  /**
   * Load all user management data
   */
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📊 Loading user management data...');
      
      const [usersData, tenantsData, invitationsData, statsData] = await Promise.all([
        UserManagementService.getAllUsers(),
        TenantService.getAllTenants(),
        UserManagementService.getPendingInvitations(),
        UserManagementService.getUserStats()
      ]);

      setUsers(usersData);
      setTenants(tenantsData);
      setInvitations(invitationsData);
      setUserStats(statsData);
      
      console.log(`✅ Loaded ${usersData.length} users, ${invitationsData.length} invitations`);
      
    } catch (err) {
      console.error('❌ Failed to load user management data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle user invitation
   */
  const handleInviteUser = async (inviteData) => {
    try {
      console.log('📧 Inviting user:', inviteData);
      
      const result = await UserManagementService.createUserInvitation(inviteData);
      
      // Show success message with invitation link
      alert(`✅ User invited successfully!\n\nInvitation sent to: ${inviteData.email}\n\nInvitation link: ${result.invitationLink}\n\n(In production, this would be sent via email)`);
      
      // Refresh data
      await loadData();
      setShowInviteModal(false);
      
    } catch (err) {
      console.error('❌ Failed to invite user:', err);
      setError(err.message);
    }
  };

  /**
   * Handle user role update
   */
  // CHA-97 / PR-C: signature added tenantId — required by the
  // assignUserRole callable. Caller (EditUserModal at line ~504) passes
  // userData.companyId from the form state.
  const handleUpdateUserRole = async (userId, tenantId, newRole) => {
    try {
      console.log('🔄 Updating user role:', userId, 'to', newRole, 'tenant:', tenantId);

      await UserManagementService.updateUserRole(userId, tenantId, newRole);

      // Refresh users
      await loadData();

      console.log('✅ User role updated successfully');
    } catch (err) {
      console.error('❌ Failed to update user role:', err);
      setError(err.message);
    }
  };

  /**
   * Handle user status toggle.
   *
   * CHA-97 / PR-C: user shape from listAllUsers callable uses
   * `disabled` (boolean from Firebase Auth) and `tenantId`. The legacy
   * `status` string and `companyId` field are gone.
   */
  const handleToggleUserStatus = async (user) => {
    try {
      const isActive = !user.disabled;

      if (isActive) {
        await UserManagementService.deactivateUser(user.uid, user.tenantId);
      } else {
        await UserManagementService.reactivateUser(user.uid, user.tenantId);
      }

      // Refresh users
      await loadData();

      console.log(`✅ User ${isActive ? 'deactivated' : 'reactivated'} successfully`);
    } catch (err) {
      console.error('❌ Failed to toggle user status:', err);
      setError(err.message);
    }
  };

  /**
   * Filter users based on search and filters.
   *
   * CHA-97 / PR-C field shape: displayName (with email fallback) +
   * roleId + tenantId + disabled. Search matches displayName OR email.
   */
  const filteredUsers = users.filter(user => {
    // Search filter. Codex Round 3 P2: orphan rows have email: null —
    // guard the .toLowerCase() call. nameForSearch is already null-safe
    // via the `|| ''` fallback.
    const nameForSearch = (user.displayName || user.email || '').toLowerCase();
    const emailForSearch = (user.email || '').toLowerCase();
    if (searchTerm && !emailForSearch.includes(searchTerm.toLowerCase()) &&
        !nameForSearch.includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Role filter
    if (selectedRole !== 'all' && user.roleId !== selectedRole) {
      return false;
    }

    // Tenant filter
    if (selectedTenant !== 'all' && user.tenantId !== selectedTenant) {
      return false;
    }

    return true;
  });

  /**
   * Get tenant name by ID
   */
  const getTenantName = (companyId) => {
    if (!companyId) return 'No Tenant';
    const tenant = tenants.find(t => t.id === companyId);
    return tenant ? tenant.name : companyId;
  };

  /**
   * Format date for display
   */
  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  /**
   * Format time ago
   */
  const formatTimeAgo = (date) => {
    if (!date) return 'Never';
    
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    
    return formatDate(date);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <div className="p-3 bg-rose-100 rounded-full w-fit mx-auto mb-4">
          <AlertCircle className="text-rose-600" size={24} />
        </div>
        <h3 className="text-lg font-medium text-slate-800 mb-2">Error Loading Users</h3>
        <p className="text-slate-600 mb-6 max-w-md mx-auto">{error}</p>
        <button
          onClick={loadData}
          className="bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700 transition-colors text-sm"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Users"
          value={userStats.total || 0}
          icon={Users}
          color="blue"
          change={`+${userStats.recentlyActive || 0} active this week`}
        />
        <StatsCard
          title="Active Users"
          value={userStats.active || 0}
          icon={UserCheck}
          color="green"
          change={`${userStats.inactive || 0} inactive`}
        />
        <StatsCard
          title="Pending Invitations"
          value={userStats.pendingInvitations || 0}
          icon={Mail}
          color="amber"
          change="Awaiting response"
        />
        <StatsCard
          title="Recent Activity"
          value={userStats.recentlyActive || 0}
          icon={Clock}
          color="purple"
          change="Last 7 days"
        />
      </div>

      {/* Header and Controls */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold text-slate-800">User Management</h2>
          <p className="text-sm text-slate-600">
            Manage users, invitations, and permissions across all tenants
          </p>
        </div>
        
        <button
          onClick={() => setShowInviteModal(true)}
          className="bg-slate-800 text-white px-4 py-2.5 rounded-lg hover:bg-slate-700 transition-all duration-200 flex items-center gap-2 text-sm font-medium shadow-sm"
        >
          <Plus size={16} />
          Invite User
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-white rounded-xl p-1 border border-slate-200/60 w-fit">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
            activeTab === 'users'
              ? 'bg-slate-800 text-white shadow-sm'
              : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
          }`}
        >
          <Users size={16} />
          Users ({users.length})
        </button>
        <button
          onClick={() => setActiveTab('invitations')}
          className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
            activeTab === 'invitations'
              ? 'bg-slate-800 text-white shadow-sm'
              : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
          }`}
        >
          <Mail size={16} />
          Invitations ({invitations.length})
        </button>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-xl border border-slate-200/60 p-6">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                  <input
                    type="text"
                    placeholder="Search users by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2.5 w-full border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 text-sm"
                  />
                </div>
              </div>

              {/* Role Filter */}
              <div className="min-w-[150px]">
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 text-sm"
                >
                  <option value="all">All Roles</option>
                  <option value="platform_owner">Platform Owner</option>
                  <option value="admin">Administrator</option>
                  <option value="manager">Manager</option>
                  <option value="user">User</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>

              {/* Tenant Filter */}
              <div className="min-w-[150px]">
                <select
                  value={selectedTenant}
                  onChange={(e) => setSelectedTenant(e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 text-sm"
                >
                  <option value="all">All Tenants</option>
                  {tenants.map(tenant => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                  <option value="">No Tenant</option>
                </select>
              </div>
            </div>

            {/* Active Filters Display */}
            {(searchTerm || selectedRole !== 'all' || selectedTenant !== 'all') && (
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
                <span className="text-xs text-slate-500">Active filters:</span>
                {searchTerm && (
                  <FilterTag
                    label={`Search: "${searchTerm}"`}
                    onRemove={() => setSearchTerm('')}
                  />
                )}
                {selectedRole !== 'all' && (
                  <FilterTag
                    label={`Role: ${UserManagementService.formatRoleName(selectedRole)}`}
                    onRemove={() => setSelectedRole('all')}
                  />
                )}
                {selectedTenant !== 'all' && (
                  <FilterTag
                    label={`Tenant: ${getTenantName(selectedTenant)}`}
                    onRemove={() => setSelectedTenant('all')}
                  />
                )}
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedRole('all');
                    setSelectedTenant('all');
                  }}
                  className="text-xs text-slate-500 hover:text-slate-700 underline ml-2"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-16">
                <div className="p-3 bg-slate-100 rounded-full w-fit mx-auto mb-4">
                  <Users className="text-slate-400" size={24} />
                </div>
                <h3 className="text-lg font-medium text-slate-800 mb-2">No Users Found</h3>
                <p className="text-slate-600 mb-6 max-w-md mx-auto">
                  {searchTerm || selectedRole !== 'all' || selectedTenant !== 'all'
                    ? 'Try adjusting your filters to see more users'
                    : 'Invite your first user to get started'
                  }
                </p>
                {!(searchTerm || selectedRole !== 'all' || selectedTenant !== 'all') && (
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700 transition-colors text-sm"
                  >
                    Invite First User
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200/60">
                      <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">User</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Role</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Tenant</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Status</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Last Login</th>
                      <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Created</th>
                      <th className="text-right px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user, index) => (
                      <UserRow
                        key={user.uid}
                        user={user}
                        tenantName={getTenantName(user.tenantId)}
                        formatDate={formatDate}
                        formatTimeAgo={formatTimeAgo}
                        onEditUser={(user) => {
                          setSelectedUser(user);
                          setShowEditUserModal(true);
                        }}
                        onToggleStatus={() => handleToggleUserStatus(user)}
                        onViewUser={(user) => {
                          setSelectedUser(user);
                          setShowUserModal(true);
                        }}
                        isLast={index === filteredUsers.length - 1}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Invitations Tab */}
      {activeTab === 'invitations' && (
        <InvitationsTab
          invitations={invitations}
          tenants={tenants}
          getTenantName={getTenantName}
          formatDate={formatDate}
          formatTimeAgo={formatTimeAgo}
          onRefresh={loadData}
        />
      )}

      {/* Modals */}
      {showInviteModal && (
        <InviteUserModal
          tenants={tenants}
          onClose={() => setShowInviteModal(false)}
          onSubmit={handleInviteUser}
        />
      )}

      {showEditUserModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          tenants={tenants}
          onClose={() => {
            setShowEditUserModal(false);
            setSelectedUser(null);
          }}
          onSubmit={async (userData) => {
            await handleUpdateUserRole(userData.uid, userData.tenantId, userData.roleId);
            setShowEditUserModal(false);
            setSelectedUser(null);
          }}
        />
      )}

      {showUserModal && selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          tenantName={getTenantName(selectedUser.companyId)}
          onClose={() => {
            setShowUserModal(false);
            setSelectedUser(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Stats Card Component
 */
function StatsCard({ title, value, icon: Icon, color, change }) {
  const colorClasses = {
    blue: 'text-blue-600 bg-blue-100',
    green: 'text-green-600 bg-green-100',
    amber: 'text-amber-600 bg-amber-100',
    purple: 'text-purple-600 bg-purple-100',
    rose: 'text-rose-600 bg-rose-100'
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200/60 p-6">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon size={20} />
        </div>
        <div className="flex-1">
          <p className="text-sm text-slate-600 mb-1">{title}</p>
          <p className="text-2xl font-semibold text-slate-900">{value}</p>
          {change && (
            <p className="text-xs text-slate-500 mt-1">{change}</p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Filter Tag Component
 */
function FilterTag({ label, onRemove }) {
  return (
    <div className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs">
      <span className="text-slate-700">{label}</span>
      <button
        onClick={onRemove}
        className="text-slate-400 hover:text-slate-600"
      >
        ×
      </button>
    </div>
  );
}

/**
 * User Row Component
 */
function UserRow({ user, tenantName, formatDate, formatTimeAgo, onEditUser, onToggleStatus, onViewUser, isLast }) {
  const [showDropdown, setShowDropdown] = useState(false);

  const getStatusColor = (status) => {
    if (status === 'inactive') return 'bg-rose-100 text-rose-700';
    return 'bg-emerald-100 text-emerald-700';
  };

  const getRoleColor = (role) => {
    const colors = {
      platform_owner: 'bg-purple-100 text-purple-700',
      admin: 'bg-blue-100 text-blue-700',
      manager: 'bg-green-100 text-green-700',
      user: 'bg-slate-100 text-slate-700',
      viewer: 'bg-amber-100 text-amber-700'
    };
    return colors[role] || 'bg-slate-100 text-slate-700';
  };

  return (
    <tr 
      className={`hover:bg-slate-50/50 transition-colors group ${
        isLast ? '' : 'border-b border-slate-100'
      }`}
    >
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-slate-600">
              {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            {/* Codex Round 3 P2: orphan rows have email: null +
                displayName: null. Fall through to a generic placeholder
                so the table renders the orphan for cleanup rather than
                crashing on .split(...). Same pattern at the two other
                render sites below + the search filter above. */}
            <div className="font-medium text-slate-900">
              {user.displayName || (user.email && user.email.split('@')[0]) || 'Orphan user'}
            </div>
            <div className="text-sm text-slate-500">{user.email}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${getRoleColor(user.roleId)}`}>
          {UserManagementService.formatRoleName(user.roleId)}
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Building2 size={14} className="text-slate-400" />
          {tenantName}
        </div>
      </td>
      <td className="px-6 py-4">
        <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${getStatusColor(user.disabled ? 'inactive' : 'active')}`}>
          {user.disabled ? 'Inactive' : 'Active'}
        </span>
      </td>
      <td className="px-6 py-4 text-sm text-slate-600">
        {formatTimeAgo(user.lastLogin)}
      </td>
      <td className="px-6 py-4 text-sm text-slate-600">
        {formatDate(user.createdAt)}
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center justify-end gap-1">
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all duration-200"
            >
              <MoreVertical size={14} />
            </button>
            
            {showDropdown && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowDropdown(false)}
                />
                <div className="absolute right-0 top-10 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20">
                  <button
                    onClick={() => {
                      onViewUser(user);
                      setShowDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <Eye size={14} />
                    View Details
                  </button>
                  {user.roleId !== 'platform_owner' && (
                    <>
                      <button
                        onClick={() => {
                          onEditUser(user);
                          setShowDropdown(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                      >
                        <Edit3 size={14} />
                        Edit Role
                      </button>
                      {/* Codex Round 6 P2: hide activate/deactivate for
                          global platform users (platform_owner /
                          platform_admin). They have tenantId: null in
                          roleAssignments, which the setUserStatus callable
                          rejects with invalid-argument. Status changes for
                          platform-scope users belong in a dedicated
                          platform-management surface; doing it here would
                          require a separate authz path. */}
                      {user.tenantId !== null && (
                        <button
                          onClick={() => {
                            onToggleStatus(user);
                            setShowDropdown(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                        >
                          {user.disabled ? (
                            <>
                              <UserCheck size={14} />
                              Activate User
                            </>
                          ) : (
                            <>
                              <UserX size={14} />
                              Deactivate User
                            </>
                          )}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

/**
 * Invitations Tab Component
 */
function InvitationsTab({ invitations, tenants, getTenantName, formatDate, formatTimeAgo, onRefresh }) {
  const copyInviteLink = (invitation) => {
    const baseUrl = window.location.origin;
    const inviteLink = `${baseUrl}/invite/${invitation.id}`;
    
    navigator.clipboard.writeText(inviteLink).then(() => {
      alert('Invitation link copied to clipboard!');
    }).catch(() => {
      alert(`Invitation link: ${inviteLink}`);
    });
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden">
        {invitations.length === 0 ? (
          <div className="text-center py-16">
            <div className="p-3 bg-slate-100 rounded-full w-fit mx-auto mb-4">
              <Mail className="text-slate-400" size={24} />
            </div>
            <h3 className="text-lg font-medium text-slate-800 mb-2">No Pending Invitations</h3>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              All invitations have been accepted or expired
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200/60">
                  <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Invited User</th>
                  <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Role</th>
                  <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Tenant</th>
                  <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Invited By</th>
                  <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Created</th>
                  <th className="text-left px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Expires</th>
                  <th className="text-right px-6 py-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map((invitation, index) => (
                  <tr 
                    key={invitation.id}
                    className={`hover:bg-slate-50/50 transition-colors ${
                      index === invitations.length - 1 ? '' : 'border-b border-slate-100'
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-amber-100 to-amber-200 rounded-full flex items-center justify-center">
                          <Mail size={14} className="text-amber-600" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{invitation.email}</div>
                          {invitation.name && (
                            <div className="text-sm text-slate-500">{invitation.name}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-700">
                        {UserManagementService.formatRoleName(invitation.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {getTenantName(invitation.companyId)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {invitation.invitedBy}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {formatDate(invitation.createdAt)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {formatTimeAgo(invitation.expiresAt)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => copyInviteLink(invitation)}
                          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all duration-200"
                          title="Copy Invitation Link"
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          onClick={() => {
                            const baseUrl = window.location.origin;
                            const inviteLink = `${baseUrl}/invite/${invitation.id}`;
                            window.open(inviteLink, '_blank');
                          }}
                          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all duration-200"
                          title="Open Invitation Link"
                        >
                          <ExternalLink size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Invite User Modal Component
 */
function InviteUserModal({ tenants, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    companyId: '',
    role: 'user',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const availableRoles = UserManagementService.getAvailableRoles();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email.trim() || !formData.companyId) {
      setError('Email and tenant are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit({
        ...formData,
        invitedBy: 'Platform Admin'
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full p-8 shadow-xl border border-slate-200/60">
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Invite New User</h2>
        <p className="text-sm text-slate-600 mb-8">Send an invitation to join the platform</p>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 transition-all duration-200"
                placeholder="user@example.com"
                disabled={loading}
                required
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">
                Full Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 transition-all duration-200"
                placeholder="John Doe"
                disabled={loading}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">
                Tenant *
              </label>
              <select
                value={formData.companyId}
                onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 transition-all duration-200"
                disabled={loading}
                required
              >
                <option value="">Select tenant...</option>
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">
                Role *
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 transition-all duration-200"
                disabled={loading}
              >
                {availableRoles.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Welcome Message (Optional)
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 transition-all duration-200 resize-none"
              rows={3}
              placeholder="Welcome to our platform! We're excited to have you on the team."
              disabled={loading}
            />
          </div>

          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
              <p className="text-rose-800 text-sm">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-all duration-200 text-sm font-medium"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-slate-800 text-white rounded-xl hover:bg-slate-700 transition-all duration-200 disabled:opacity-50 text-sm font-medium shadow-sm"
              disabled={loading}
            >
              {loading ? 'Sending Invitation...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Edit User Modal Component
 */
function EditUserModal({ user, tenants, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    uid: user.uid,
    roleId: user.roleId,
    tenantId: user.tenantId || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const availableRoles = UserManagementService.getAvailableRoles();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError(null);

    try {
      await onSubmit(formData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 shadow-xl border border-slate-200/60">
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Edit User</h2>
        <p className="text-sm text-slate-600 mb-8">Update user role and permissions</p>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-slate-50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-slate-600">
                  {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="font-medium text-slate-900">
                  {user.displayName || (user.email && user.email.split('@')[0]) || 'Orphan user'}
                </div>
                <div className="text-sm text-slate-500">{user.email}</div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Role
            </label>
            <select
              value={formData.roleId}
              onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-400/20 focus:border-slate-400 transition-all duration-200"
              disabled={loading}
            >
              {availableRoles.map(role => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500">
              {availableRoles.find(r => r.value === formData.roleId)?.description}
            </p>
          </div>

          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
              <p className="text-rose-800 text-sm">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-all duration-200 text-sm font-medium"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-slate-800 text-white rounded-xl hover:bg-slate-700 transition-all duration-200 disabled:opacity-50 text-sm font-medium shadow-sm"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * User Details Modal Component
 */
function UserDetailsModal({ user, tenantName, onClose }) {
  return (
    <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-8 shadow-xl border border-slate-200/60">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl font-semibold text-slate-800">User Details</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            ×
          </button>
        </div>
        
        <div className="space-y-6">
          {/* User Header */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center">
              <span className="text-xl font-medium text-slate-600">
                {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                {user.displayName || (user.email && user.email.split('@')[0]) || 'Orphan user'}
              </h3>
              <p className="text-slate-600">{user.email}</p>
              <p className="text-sm text-slate-500">
                {UserManagementService.formatRoleName(user.roleId)} at {tenantName}
              </p>
            </div>
          </div>

          {/* User Details Grid */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-3">Account Information</h4>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wide">Status</label>
                  <p className="text-sm text-slate-900">
                    <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                      user.disabled ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      {user.disabled ? 'Inactive' : 'Active'}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wide">Role</label>
                  <p className="text-sm text-slate-900">{UserManagementService.formatRoleName(user.roleId)}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wide">Tenant</label>
                  <p className="text-sm text-slate-900">{tenantName}</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-3">Activity</h4>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wide">Created</label>
                  <p className="text-sm text-slate-900">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wide">Last Login</label>
                  <p className="text-sm text-slate-900">
                    {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-wide">Last Updated</label>
                  <p className="text-sm text-slate-900">
                    {user.updatedAt ? new Date(user.updatedAt).toLocaleDateString() : '—'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions */}
          {user.permissions && (
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-3">Permissions</h4>
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(user.permissions).map(([area, perms]) => (
                    <div key={area}>
                      <label className="text-xs text-slate-500 uppercase tracking-wide capitalize">
                        {area.replace('_', ' ')}
                      </label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {perms.map((perm, index) => (
                          <span key={index} className="px-2 py-1 text-xs bg-white rounded border text-slate-600">
                            {perm}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-6 mt-6 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}